import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import AllergyService from "./AllergyService";
import { ALG_V2_REFRESH } from "./events";
import ModalPortal from "../common/ModalPortal";
import MetaTooltip from "../medicines/MetaTooltip";

/**
 * "View trend" modal — px-matched to the mock: title = allergen name;
 * a subheader "Audit · N entries"; six-column table:
 *   TIMESTAMP · FIRST NOTED · REACTION · LAST REACTION · DESCRIPTION · REPORTED BY
 * plus a trailing flag button per row.
 *
 * Marked-incorrect rows render struck-through / dimmed with a red flag.
 * Clicking a grey flag opens ReviewEntryDialog; clicking a red flag
 * opens MarkedIncorrectDialog (audit-only display of who marked it).
 * Success card ("Entry marked as incorrect") shown after the wire call
 * confirms; refresh event fires so the underlying list picks up the
 * server's version bump.
 */

// "27 Jul 2026\n02:10 pm" — two-line timestamp shown in the leftmost
// column of the mock. Always returns {date, time} strings; either may be
// empty on bad input so the caller can render an em-dash safely.
const formatTimestamp = (iso) => {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: "—", time: "" };
  return {
    date: d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase(),
  };
};

const formatTimeOnly = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
};

const formatReactions = (rs) =>
  (rs || [])
    .map((r) => (typeof r === "string" ? r : (r && r.name) || ""))
    .filter(Boolean)
    .join(", ") || "—";

const FlagIcon = ({ marked }) => (
  <svg
    viewBox="0 0 20 20"
    fill={marked ? "#ef4444" : "none"}
    stroke={marked ? "#ef4444" : "#9ca3af"}
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <path d="M4 3v14M4 3h9l-1.5 4L14 11H4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
FlagIcon.propTypes = { marked: PropTypes.bool };

// —— Review dialog (unmarked → offer Mark as incorrect) ————————————
const ReviewEntryDialog = ({ submitting, onCancel, onConfirm }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={submitting ? null : onCancel}>
    <div
      className="alg-review"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-review-head">
        <h3 className="alg-review-title">Review this entry</h3>
        <button
          type="button"
          className="alg-review-close"
          onClick={onCancel}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <p className="alg-review-body">You can mark the whole entry as incorrect.</p>
      <p className="alg-review-body">
        <strong>Please note: Marking incorrect is allowed only once.</strong>
      </p>
      <footer className="alg-review-foot">
        <button
          type="button"
          className="alg-review-mark"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? "Marking…" : "Mark as incorrect"}
        </button>
      </footer>
    </div>
  </div>
);
ReviewEntryDialog.propTypes = {
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

// —— Success card ("Entry marked as incorrect") ———————————————————————
const SuccessDialog = ({ onDone }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onDone}>
    <div
      className="alg-success"
      onMouseDown={(e) => e.stopPropagation()}
      role="alertdialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="alg-review-close"
        onClick={onDone}
        aria-label="Close"
      >
        ×
      </button>
      <span className="alg-success-medallion" aria-hidden="true">
        <FlagIcon marked />
      </span>
      <h3 className="alg-success-title">Entry marked as incorrect</h3>
      <p className="alg-success-body">This entry remains in the audit history.</p>
      <button type="button" className="alg-success-done" onClick={onDone}>
        Done
      </button>
    </div>
  </div>
);
SuccessDialog.propTypes = { onDone: PropTypes.func.isRequired };

// —— Marked-incorrect audit (BY / ON / AT) ————————————————————————————
const MarkedIncorrectDialog = ({ row, onClose }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onClose}>
    <div
      className="alg-marked"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-marked-head">
        <span className="alg-marked-eyebrow">
          <FlagIcon marked /> MARKED INCORRECT
        </span>
        <button
          type="button"
          className="alg-review-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <dl className="alg-marked-dl">
        <dt>By</dt>
        <dd>
          {row.markedIncorrectByName ||
            (row.markedIncorrectBy && `${row.markedIncorrectBy.who} #${row.markedIncorrectBy.whoId}`) ||
            "—"}
        </dd>
        <dt>On</dt>
        <dd>{row.markedIncorrectAt ? formatShortDate(row.markedIncorrectAt) : "—"}</dd>
        <dt>At</dt>
        <dd>{row.markedIncorrectAt ? formatTimeOnly(row.markedIncorrectAt) : "—"}</dd>
      </dl>
    </div>
  </div>
);
MarkedIncorrectDialog.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

const TrendModal = ({ patientId, allergyId, allergyName, onClose }) => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null); // history row → dialog
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const svc = new AllergyService();
    svc.trend(
      patientId,
      allergyId,
      (res) => {
        setTrend(res || { history: [] });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load trend");
        setLoading(false);
      }
    );
  }, [patientId, allergyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const rows = (trend && trend.history) || [];
  // The most-recent audit row IS the live allergy state — flagging it as
  // "incorrect" would contradict what the main table renders. Backend
  // appends by insertion order, so the highest `index` is the live row;
  // that entry stays flag-less. All prior (past) entries stay flaggable.
  const liveIndex = rows.length > 0 ? Math.max(...rows.map((r) => r.index)) : -1;
  // Descending order of timestamp (newest first) — the live row sits at
  // the top of the table and older entries scroll below.
  const displayRows = [...rows].sort((a, b) => b.index - a.index);

  const submitMarkIncorrect = () => {
    if (!flagTarget || submitting) return;
    setSubmitting(true);
    const svc = new AllergyService();
    svc.markHistoryIncorrect(
      patientId,
      allergyId,
      flagTarget.index,
      (trend && trend.version) || 0,
      () => {
        setSubmitting(false);
        setFlagTarget(null);
        setSuccessOpen(true);
        EventService.emit(ALG_V2_REFRESH);
        load();
      },
      (err) => {
        setSubmitting(false);
        setFlagTarget(null);
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Mark failed — please retry");
      }
    );
  };

  // Which dialog to show for the currently-clicked flag.
  const flagIsMarked = flagTarget && flagTarget.markedIncorrect;

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={submitting ? null : onClose}>
        <div
          className="cc-modal alg-trend"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="alg-trend-title"
        >
          <header className="alg-trend-head">
            <h2 id="alg-trend-title" className="alg-trend-title">
              {(allergyName || "").toUpperCase()}
            </h2>
            <button
              type="button"
              className="alg-trend-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </header>
  
          <div className="alg-trend-sub">
            <span className="alg-trend-tab">Audit</span>
            <span className="alg-trend-count">
              &middot; {rows.length} {rows.length === 1 ? "entry" : "entries"}
            </span>
          </div>
  
          {loading && <div className="alg-trend-empty">Loading trend…</div>}
          {error && <div className="alg-trend-empty is-error">{error}</div>}
  
          {!loading && !error && rows.length === 0 && (
            <div className="alg-trend-empty">No audit history yet.</div>
          )}
  
          {!loading && !error && rows.length > 0 && (
            <div className="alg-trend-table" role="table">
              <div className="alg-trend-thead" role="row">
                <span role="columnheader">Timestamp</span>
                <span role="columnheader">First noted</span>
                <span role="columnheader">Reaction</span>
                <span role="columnheader">Last reaction</span>
                <span role="columnheader">Description</span>
                <span role="columnheader">Reported by</span>
                <span role="columnheader" aria-label="Actions" />
              </div>
  
              {displayRows.map((r) => {
                const ts = formatTimestamp(r.recordedAt);
                const isMarked = !!r.markedIncorrect;
                const isLive = r.index === liveIndex;
                return (
                  <div
                    className={`alg-trend-row${isMarked ? " is-marked" : ""}${isLive ? " is-live" : ""}`}
                    role="row"
                    key={r.index}
                  >
                    <span className="alg-trend-cell alg-trend-ts" role="cell">
                      <span>{ts.date}</span>
                      {ts.time && <span className="alg-trend-ts-time">{ts.time}</span>}
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {r.firstNoted ? formatShortDate(r.firstNoted) : "—"}
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {formatReactions(r.reactions)}
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {/* Date-only — Reactions column already lists the
                          names, so combining them here just duplicated
                          the data across two adjacent cells. */}
                      {r.lastReaction ? formatShortDate(r.lastReaction) : "—"}
                    </span>
                    <span className="alg-trend-cell alg-trend-desc" role="cell">
                      {/* 3-line clamp + white hover tooltip; only reveals
                          when the text is actually being truncated. Same
                          pattern the section table uses. */}
                      <MetaTooltip text={r.description || ""} lines={3} />
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {r.reportedByName ||
                        (r.reportedBy && `${r.reportedBy.who} #${r.reportedBy.whoId}`) ||
                        "—"}
                    </span>
                    <span className="alg-trend-cell alg-trend-flag-cell" role="cell">
                      {/* Live row shows nothing in the flag column — the
                          record is the current allergy and can only be
                          corrected via Edit / Delete on the main table. */}
                      {!isLive && (
                        <button
                          type="button"
                          className="alg-trend-flag"
                          onClick={() => setFlagTarget(r)}
                          aria-label={isMarked ? "View incorrect-mark audit" : "Review this entry"}
                          title={isMarked ? "View incorrect-mark audit" : "Review this entry"}
                        >
                          <FlagIcon marked={isMarked} />
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
  
        {flagTarget && !flagIsMarked && !successOpen && (
          <ReviewEntryDialog
            submitting={submitting}
            onCancel={() => setFlagTarget(null)}
            onConfirm={submitMarkIncorrect}
          />
        )}
        {flagTarget && flagIsMarked && (
          <MarkedIncorrectDialog
            row={flagTarget}
            onClose={() => setFlagTarget(null)}
          />
        )}
        {successOpen && <SuccessDialog onDone={() => setSuccessOpen(false)} />}
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  allergyId: PropTypes.string.isRequired,
  allergyName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
